<script setup>
import { useForm } from "@inertiajs/inertia-vue3";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import RadioButton from "primevue/radiobutton";
import { ref } from "vue";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";

const emits = defineEmits(["next"]);
const props = defineProps([
    "form",
    "errors",
    "v$",
    "toast",
]);
const yesNo = ref([
    {
        label: "ja",
        value: true,
    },
    {
        label: "nein",
        value: false,
    }
]);

const INBODY_USER_ID_MAX_LENGTH = 20

// No whitespace allowed (also when pasted), max 20 characters. The server validates the same.
const sanitizeInbodyUserId = (event) => {
    const clean = event.target.value.replace(/\s+/g, '').slice(0, INBODY_USER_ID_MAX_LENGTH)

    if (event.target.value !== clean) {
        event.target.value = clean
    }

    props.form.inbody_user_id = clean
    props.v$.form.inbody_user_id.$touch()
}

const inbodyFile = ref('')

const inbodyForm = useForm({
    file: null,
})

const uploadInbodyFile = (e) => {
    const file = e.target?.files[0]

    if (! file) {
        return
    }

    inbodyForm.file = file

    inbodyForm.post('/parse-inbody-file', {
        preserveState: true,
        preserveScroll: true,

        onSuccess: (resp) => {
            props.form.fat_percentage = resp.props.fatPercentage
            props.form.visceral_fat_area = resp.props.visceralFatArea

            props.toast.add({
                severity: "success",
                summary: "Aktualisiert",
                life: 5000,
            });
        }
    })
}
</script>

<template>
    <div class="lato p-7 rounded my-2">
        <div class="mb-3">
            <div class="font-bold mb-2">Möchtest du dein Gewicht reduzieren?</div>

            <div class="grid grid-cols-3 gap-4">
                <div
                    class="flex items-center"
                    v-for="(item, i) in yesNo"
                    :key="i"
                >
                    <div class="flex items-center gap-4">
                        <RadioButton
                            :invalid="v$.form.want_to_reduce_weight.$error"
                            v-model="form.want_to_reduce_weight"
                            :value="item.value"
                        />

                        <div class="">{{ item.label }}</div>
                    </div>
                </div>

                <div class="flex justify-end">
                    <Button label="Inbody-Datei hochladen"
                            class="min-w-[100px] !border-none"
                            severity="secondary"
                            :loading="inbodyForm.processing"
                            :disabled="inbodyForm.processing"
                            @click="inbodyFile.click()"
                    >
                    </Button>

                    <input ref="inbodyFile"
                           type="file"
                           accept=".pdf"
                           hidden
                           @change="uploadInbodyFile" />
                </div>
            </div>

            <div v-if="v$.form.want_to_reduce_weight.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.want_to_reduce_weight.$errors[0].$message }}
            </div>
        </div>

        <div class="mb-2">
            <div class="flex items-center gap-2">
                <label class="">Um wie viele Kilogramm</label>

                <div>
                    <InputGroup>
                        <InputText
                            :invalid="v$.form.reduce_weight.$error"
                            v-model="form.reduce_weight"
                            class="max-w-[150px]"
                            type="number"
                            step=".01"
                            @input="v$.form.reduce_weight.$touch"
                            @blur="v$.form.reduce_weight.$touch"
                        />

                        <InputGroupAddon>kg</InputGroupAddon>
                    </InputGroup>
                </div>
            </div>

            <div v-if="v$.form.reduce_weight.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.reduce_weight.$errors[0].$message }}
            </div>
        </div>

        <div class="mb-5">
            <div class="flex items-center gap-2">
                <label>Körperfett in % (Werte von InBody)</label>

                <div>
                    <InputGroup>
                        <InputText
                            :invalid="v$.form.fat_percentage.$error"
                            v-model="form.fat_percentage"
                            class="max-w-[150px]"
                            type="number"
                            step=".01"
                            @input="v$.form.fat_percentage.$touch"
                            @blur="v$.form.fat_percentage.$touch"
                        />

                        <InputGroupAddon>%</InputGroupAddon>
                    </InputGroup>
                </div>
            </div>

            <div v-if="v$.form.fat_percentage.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.fat_percentage.$errors[0].$message }}
            </div>
        </div>

        <div>
            <div class="font-bold mb-2">Viszeraler Fettbereich</div>

            <div>
                <InputGroup>
                    <InputText
                        :invalid="v$.form.visceral_fat_area.$error"
                        v-model="form.visceral_fat_area"
                        class="max-w-[150px]"
                        type="number"
                        step=".01"
                        @input="v$.form.visceral_fat_area.$touch"
                        @blur="v$.form.visceral_fat_area.$touch"
                    />

                    <InputGroupAddon>cm2</InputGroupAddon>
                </InputGroup>
            </div>

            <div v-if="v$.form.visceral_fat_area.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.visceral_fat_area.$errors[0].$message }}
            </div>
        </div>

        <div class="mt-5">
            <div class="font-bold mb-2">InBody User-ID</div>

            <div>
                <InputText
                    :invalid="v$.form.inbody_user_id.$error || Boolean(errors?.inbody_user_id)"
                    v-model="form.inbody_user_id"
                    class="max-w-[250px]"
                    type="text"
                    :maxlength="INBODY_USER_ID_MAX_LENGTH"
                    @keydown.space.prevent
                    @input="sanitizeInbodyUserId"
                    @blur="v$.form.inbody_user_id.$touch"
                />
            </div>

            <div class="text-sm text-gray-500 mt-1">
                User-ID aus den LookinBody-Mitgliederdetails eintragen.
            </div>

            <div v-if="errors?.inbody_user_id" class="text-sm text-[#dc2626]">
                {{ [].concat(errors.inbody_user_id)[0] }}
            </div>
        </div>

        <div class="my-10">
            <div class="mb-3">
                <div class="font-bold mb-2">
                    Möchtest du gezielt Fettdepots abbauen?
                </div>

                <div class="grid grid-cols-3 gap-4">
                    <div
                        class=""
                        v-for="(item, i) in yesNo"
                        :key="i"
                    >
                        <div class="flex items-center gap-4">
                            <RadioButton
                                :invalid="v$.form.want_fat_deposits.$error"
                                v-model="form.want_fat_deposits"
                                :value="item.value"
                            />
                            <div class="">{{ item.label }}</div>
                        </div>
                    </div>
                </div>

                <div v-if="v$.form.want_fat_deposits.$error" class="text-sm text-[#dc2626]">
                    {{ v$.form.want_fat_deposits.$errors[0].$message }}
                </div>
            </div>

            <div class="grid grid-cols-3 gap-4">
                <div
                    class=""
                    v-for="(item, i) in ['Bauch', 'Hüfte', 'Gesäss']"
                    :key="i"
                >
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :invalid="v$.form.fat_deposit_areas.$error"
                            v-model="form.fat_deposit_areas"
                            :value="item"
                            @change="v$.form.fat_deposit_areas.$touch"
                        />
                        <div class="">{{ item }}</div>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-3 gap-4 my-7">
                <div
                    class=""
                    v-for="(item, i) in ['Oberschenkel', 'Unterarm']"
                    :key="i"
                >
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :invalid="v$.form.fat_deposit_areas.$error"
                            v-model="form.fat_deposit_areas"
                            :value="item"
                            @change="v$.form.fat_deposit_areas.$touch"
                        />
                        <div class="">{{ item }}</div>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-3 gap-4">
                <div class="">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :invalid="v$.form.fat_deposit_areas.$error"
                            v-model="form.fat_deposit_areas"
                            value="Andere Gebiete"
                            @change="v$.form.fat_deposit_areas.$touch"
                        />
                        <div class="">Andere Gebiete</div>
                    </div>
                </div>

                <div class="col-span-2">
                    <InputText
                        v-model="form.fat_deposit_other"
                        type="text"
                        fluid
                    />
                </div>
            </div>

            <div v-if="v$.form.fat_deposit_areas.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.fat_deposit_areas.$errors[0].$message }}
            </div>
        </div>

        <div class="flex mt-10 justify-end">
            <Button
                label="weiter"
                class="min-w-[100px] !bg-black !text-[#FFF053] !border-none"
                @click="emits('next', 'anamnese', 'goals')"
            />
        </div>
    </div>
</template>
